import React, { useState } from "react";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Sections";
import { Reveal } from "@/components/Reveal";
import { Field, TextArea } from "@/components/Field";
import { FormSuccess } from "@/components/FormSuccess";
import { CtaPrimary } from "@/components/CTA";

const init = {
  name: "", company: "", phone: "", email: "",
  country: "", zip: "", brand: "",
  model_number: "", machine_number: "", spare_parts_list: "",
};

export default function Quote() {
  useSEO({
    title: "Request a Quote | Mining Spare Parts | BlackSilex",
    description: "Request a quote for mining equipment spare parts. Provide your brand, model and the parts you require — we respond with origin, lead time and pricing.",
    path: "/quote",
  });
  const [form, setForm] = useState(init);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/quotes", { ...form });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="quote-page">
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "Request a Quote" }]}
        eyebrow="Procurement Request"
        titleLines={["Request", "a quote."]}
        intro="A professional quote request for mining equipment spare parts — the more detail you provide, the faster we respond with origin, lead time and pricing."
      />

      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        {done ? (
          <FormSuccess copy="Our team will review the requirement and respond with origin, lead time and pricing." />
        ) : (
          <Reveal>
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-6" data-testid="quote-form">
              <Field half label="Name" name="name" value={form.name} onChange={set} required testId="q-name" placeholder="Contact name" />
              <Field half label="Company" name="company" value={form.company} onChange={set} testId="q-company" placeholder="Company name" />
              <Field half label="Phone" name="phone" value={form.phone} onChange={set} testId="q-phone" placeholder="Direct line" />
              <Field half label="Email" name="email" type="email" value={form.email} onChange={set} required testId="q-email" placeholder="you@company.com" />
              <Field half label="Country of Delivery" name="country" value={form.country} onChange={set} testId="q-country" placeholder="Country" />
              <Field half label="ZIP / Postal Code" name="zip" value={form.zip} onChange={set} testId="q-zip" placeholder="Postal code" />
              <Field label="Brand" name="brand" value={form.brand} onChange={set} testId="q-brand" placeholder="Equipment brand" />
              <Field half label="Model Number" name="model_number" value={form.model_number} onChange={set} testId="q-model" placeholder="Model number" />
              <Field half label="Machine Number" name="machine_number" value={form.machine_number} onChange={set} testId="q-machine" placeholder="Machine / serial number" />
              <TextArea label="List of Spare Parts" name="spare_parts_list" value={form.spare_parts_list} onChange={set} rows={7} testId="q-parts" placeholder="Please enter the part numbers and quantities you require." />
              <div className="md:col-span-2 pt-4">
                <CtaPrimary type="submit" testId="q-submit">{busy ? "Submitting…" : "Request a Quote"}</CtaPrimary>
              </div>
            </form>
          </Reveal>
        )}
      </section>
    </div>
  );
}
